import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { HOME_ROUTE_FOR_ROLE } from '../routes/roleHome';
import { ImpersonationBanner } from './ImpersonationBanner';
import { SiteFooter } from './SiteFooter';
import { NavDropdown } from './NavDropdown';
import './PublicLayout.css';

/**
 * THE HEADER CHANGES WITH INTENT, not with taste.
 *
 * Once someone is looking at an event, choosing a tier or paying, every other link in the
 * header is a way to lose them. So the buying flow gets a wordmark and one item, and the
 * footer collapses to match: offering a mid checkout visitor a column of links to the
 * investor portal is the same mistake in a different place.
 *
 * Everywhere else, ONE link sits at the top level. Events is what a casual visitor came
 * for; every workspace, plus Artists and the investor entrance, lives behind a quieter
 * "For the industry" menu.
 *
 * That menu opens on CLICK, never hover. A hover menu simply does not exist on a phone,
 * and the emulated hover browsers substitute sticks open after the tap. See NavDropdown.
 *
 * That grouping is a brand decision and a compliance-shaped one at once. Voleto should
 * read as a ticketing platform to the public, and a securities offering competing for
 * attention with "Events" in the primary nav pulls against the separation 506(c) already
 * requires.
 */

/** Anything under these paths is a purchase in progress. */
const BUYING_FLOW = [/^\/events(\/|$)/, /^\/orders\//];

/** The only top-level destination. Everything else is one level down. */
const PRIMARY_NAV = [
  { to: '/events', label: 'Events' },
];

const INDUSTRY_NAV = [
  { to: '/organizer', label: 'Organizers' },
  { to: '/artists', label: 'Artists' },
  { to: '/venue', label: 'Venues' },
  { to: '/agency', label: 'Agencies' },
  { to: '/sponsor', label: 'Sponsors' },
  { to: '/invest', label: 'Invest' },
];

export function PublicLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const { pathname } = useLocation();
  const buying = BUYING_FLOW.some((pattern) => pattern.test(pathname));

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'public-header__link is-active' : 'public-header__link';

  return (
    <div className="public-layout">
      <ImpersonationBanner />
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className={buying ? 'public-header public-header--focused' : 'public-header'}>
        <div className="container public-header__inner">
          <Link to="/" className="public-header__brand display">
            Voleto&trade;
          </Link>

          <nav className="public-header__nav" aria-label="Primary">
            {buying ? (
              <NavLink to="/events" className={linkClass}>
                Events &amp; Tickets
              </NavLink>
            ) : (
              <>
                {PRIMARY_NAV.map((item) => (
                  <NavLink key={item.to} to={item.to} className={linkClass}>
                    {item.label}
                  </NavLink>
                ))}

                <NavDropdown
                  label="For the industry"
                  shortLabel="Industry"
                  items={INDUSTRY_NAV}
                />
              </>
            )}
          </nav>

          <div className="public-header__actions">
            {isAuthenticated && user ? (
              <>
                <Link to={HOME_ROUTE_FOR_ROLE[user.role]} className="button button--ghost">
                  My portal
                </Link>
                <button type="button" className="button button--ghost" onClick={() => void logout()}>
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="button button--ghost">
                  Sign in
                </Link>
                <Link to="/register" className="button button--primary">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main" className="public-main container">
        <Outlet />
      </main>

      <SiteFooter minimal={buying} />
    </div>
  );
}
